<template>
    <div>
        <vx-collapsible
            v-show="showScheduledMessagesBox"
            :toggle-msg="showScheduledMessages ? 'Hide Scheduled Messages' : 'Show Scheduled Messages'"
            :height="210"
            @toggle="showScheduledMessages = $event"
        >
            <div class="vx-flex vx-flex-col vx-gap-1 vx-px-1">
                <div class="vx-flex vx-flex-col-reverse vx-flex-1 vx-overflow-y-auto vx-py-2">
                    <div v-for="(messageGroup, key) in messageGroups" :key="key" class="vx-flex vx-flex-col vx-gap-6">
                        <div class="vx-relative vx-flex vx-items-center vx-justify-center vx-w-full vx-my-4">
                            <div
                                class="vx-absolute vx-border-0 vx-border-b vx-border-solid vx-w-full vx-border-gray-200"
                            ></div>
                            <vx-badge
                                :color="messageGroup.lastUnreadInboundMessage ? 'amber' : 'slate'"
                                borders
                                size="xs"
                                class="vx-absolute vx-text-center vx-px-3 vx-py-1 !vx-bg-slate-50 !vx-text-slate-500"
                            >
                                <strong v-if="messageGroup.lastUnreadInboundMessage">New: </strong>
                                {{ messageGroup.prettyDate }}
                            </vx-badge>
                        </div>
                        <div class="vx-flex vx-flex-col vx-gap-6">
                            <message-cloud
                                v-for="message in messageGroup.messages"
                                :key="message.id"
                                :message="message"
                                scheduled
                                @editSchedule="openEditModal(message)"
                                @mediaPlaceholderClick="($event) => {
                                    if ($event.isFileExpired) {
                                        mediaExpiredModal = true;
                                    }
                                }"
                            ></message-cloud>
                        </div>
                    </div>
                </div>
            </div>
        </vx-collapsible>
        <scheduled-message-modal
            v-model:visible="showScheduledModal"
            @scheduledMessageEdited="editScheduledMessage"
            :scheduled-message="selectedScheduledMessage"
            class="!vx-items-baseline"
        ></scheduled-message-modal>
        <vx-modal v-model:visible="mediaExpiredModal" class="vx-max-w-lg">
            <template v-slot:header>
                <div class="vx-flex vx-flex-col vx-items-center vx-justify-center vx-gap-y-2">
                    <font-awesome-icon :icon="faImage" size="2xl" class="vx-text-rose-400" />
                    <span class="vx-text-3xl vx-font-extrabold vx-text-center">Expired Media</span>
                </div>
            </template>

            <div class="vx-flex vx-flex-col vx-items-center vx-justify-center vx-gap-y-8">
                <p class="vx-text-base vx-font-normal vx-text-center vx-text-slate-500">
                    Images and videos are only kept by our MMS provider for a short time. After this time, the media
                    will disappear. Instead of deleting the message entirely, we show a placeholder icon so you can see
                    when the media was sent or received.
                </p>
                <div class="vx-flex vx-items-center vx-justify-center vx-w-full">
                    <vx-button
                        @click="mediaExpiredModal = false"
                        size="lg"
                        type="button"
                        block
                        color="primary"
                        class="lg:vx-mt-6 vx-max-w-[269px]"
                    >
                        Got It
                    </vx-button>
                </div>
            </div>
        </vx-modal>
    </div>
</template>
<script setup>
import { computed, ref } from 'vue';
import { VxCollapsible } from '@voxie/frontend-components-v3';
import scheduledMessagesClient from '~/services/scheduledMessagesClient';
import dayjs from '~/utils/dayjs';
import { isAuthErrorEcho } from '~/utils/helpers';
import { useToasted } from '~/composables/useToasted';
import { VxBadge, VxButton } from '@voxie/frontend-components';
import ScheduledMessages from '~/utils/scheduledMessages';
import ScheduledMessageModal from '~/components/scheduled-messages/modals/ScheduledMessageModal.vue';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import MessageCloud from './misc/MessageCloud.vue';
import { VxModal } from '@voxie/frontend-components';
import { faImage } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { contact } = useContactDetails();

const $toasted = useToasted();
const teamId = Spark.state.currentTeam.id;
const defaultFilters = () => {
    return {
        cursor: undefined,
        sort_dir: 'desc',
    };
};

const mediaExpiredModal = ref(false);

const messages = ref([]);
const messageGroups = computed(() => {
    const messagesToDay = ([day, group]) => {
        return {
            fullDate: day,
            prettyDate: dayjs(day).format('MMMM D, YYYY'),
            messages: group.sort((mA, mB) => mA.id - mB.id),
        };
    };

    const groups = messages.value.reduce((groups, message) => {
        const groupKey = dayjs(message.created_at).startOf('day').toString();
        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }

        groups[groupKey].push(message);

        return groups;
    }, {});

    return Object.entries(groups)
        .map(messagesToDay)
        .sort((gA, gB) => {
            return new Date(gB.fullDate).getTime() - new Date(gA.fullDate).getTime();
        });
});

const showScheduledMessagesBox = computed(() => messages.value.length);
const showScheduledMessages = ref(false);

const fetchParams = ref(defaultFilters());

const loading = ref(false);
const loadMessages = async () => {
    if (loading.value) {
        return;
    }

    loading.value = true;

    try {
        const response = await scheduledMessagesClient.getContactScheduledMessages(teamId, contact.value.id, {
            ...fetchParams.value,
        });

        messages.value = !fetchParams.value.cursor ? response.data.data : [...messages.value, ...response.data.data];

        // set next cursor for subsequent request
        const nextPageParams = new URLSearchParams(response.data.next_page_url?.split('?')?.[1]);

        fetchParams.value = {
            ...fetchParams.value,
            cursor: nextPageParams.get('cursor'),
        };
    } catch (e) {
        console.log(e);
        $toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

const showScheduledModal = ref(false);
const selectedScheduledMessage = ref(null);
const openEditModal = (scheduledMessage) => {
    selectedScheduledMessage.value = {
        ...scheduledMessage,
        contact: {
            first_name: contact.value.first_name || '',
            last_name: contact.value.last_name || '',
            phone: contact.value.phone,
        },
    };
    showScheduledModal.value = true;
};

const editScheduledMessage = async (data) => {
    await ScheduledMessages.editScheduledMessage(data, $toasted);

    // Remove old message since it will come in ScheduledMessageCreated listener.
    messages.value = messages.value.filter((m) => m.id !== data.id);
    selectedScheduledMessage.value = null;
};

loadMessages();

const echoErrors = [];
Echo.private(`scheduled_messages.${teamId}`)
    .listen('ScheduledMessageCreated', ({ contact: payloadContact, scheduled_message }) => {
        if (payloadContact.id === contact.value.id) {
            messages.value.push(scheduled_message);
        }
    })
    .listen('ScheduledMessageEnqueued', ({ contact: payloadContact, scheduled_message }) => {
        if (payloadContact.id === contact.value.id) {
            messages.value = messages.value.filter((m) => m.id !== scheduled_message.id);
        }
    })
    .error?.((e) => {
        if (echoErrors.length <= 3 && !isAuthErrorEcho(e)) {
            window?.Bugsnag?.notify(e instanceof Error ? e : new Error(JSON.stringify(e)));
            echoErrors.push(e);
        }
    });
</script>
